import { handleApiError } from '@/utils/utils'
import { getAxiosApiClientByUser } from './axiosInstance'
import { Repo } from '@/components/shared/chatbox/types'
import {
  CREDIT_COSTS,
  Feature
} from '@/app/(main)/ai-design-center/lib/constants'
// import api from "./matomo-axios";

interface Choice {
  id: number
  text: string
  description?: string
  tooltip?: string
}

interface Variant {
  id: number
  description: string
  html: string
}

interface Message {
  sender: 'bot' | 'user'
  message: string
  editMode: boolean
  timestamp: string
  choices?: Choice[]
  variants: Variant[]
}

interface ChatThread {
  threadId: string
  createdAt: string
  threadTitle: string
  messages: Message[]
}

export const updatePageContext = async (
  pageId: string,
  originalPrompt: string,
  sectionPrompts: string[],
  pageContent: string
) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/landing-page/pages/update-context',
      {
        page_id: pageId,
        original_prompt: originalPrompt,
        section_prompts: sectionPrompts,
        page_content: pageContent
      }
    )

    return response?.data?.['Updated context']
  } catch (error) {
    handleApiError(error, 'Update Page Context')
  }
}

export const getPageMessages = async (landingPageId: string) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get(
      `/landing-page/page-messages/${landingPageId}`
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Failed to fetch page messages.')
  }
}

export const savePageMessage = async (
  landingPageId: string,
  sender: string,
  message: string,
  editMode: boolean,
  choices?: { id: number; text: string }[],
  variants?: { id: number; description: string; html: string }[]
) => {
  try {
    const payload = {
      landingPageId,
      sender,
      message,
      editMode,
      choices: choices ?? [],
      variants: variants ?? []
    }
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/landing-page/page-messages',
      payload
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Failed to save page message')
  }
}

export const savePageMessageToThread = async (
  pageId: string,
  threadId: string | null,
  sender: string,
  message: string,
  editMode: boolean,
  choices?: Array<{ id: number; text: string }> | null,
  variants?: Array<{ id: number; description: string; html: string }> | null,
  repos?: Repo[]
) => {
  try {
    const payload = {
      pageId,
      threadId,
      sender,
      message,
      editMode,
      choices: choices ?? [],
      variants: variants ?? [],
      repos: repos ?? []
    }
    const apiClient = await getAxiosApiClientByUser()

    const response = await apiClient.post(
      '/landing-page/page-messages/thread',
      payload
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Failed to save message to thread')
  }
}

export const getChatThreads = async (pageId: string, editMode: boolean) => {
  try {
    const apiClient = await getAxiosApiClientByUser()

    const response = await apiClient.get(
      `/landing-page/threads/${pageId}?editMode=${editMode}`
    )

    const threads = response.data?.data
    const isSuccess = response.data?.success ?? true

    if (!Array.isArray(threads)) {
      return { success: isSuccess, data: [] }
    }

    const enhancements: Record<
      number,
      { description: string; tooltip: string }
    > = {
      1: {
        description:
          'A conversion-focused page designed for a specific marketing campaign or offer. Ideal for capturing leads or driving sales with a single, clear call to action (CTA). Landing pages do not include page navigation.',
        tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION_LANDING]} credits`
      },
      2: {
        description:
          'A complete website where all content exists on one long, scrolling page. Best for portfolios, simple business overviews, or focused narratives that flow from top to bottom.',
        tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION]} credits`
      },
      3: {
        description:
          'A traditional website structure with several distinct pages (e.g., Home, About, Services, Contact) connected by a navigation menu. Ideal for organizing larger amounts of diverse content.',
        tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION_MULTI]} credits`
      }
    }

    const formattedThreads: ChatThread[] = threads.map(
      (thread: ChatThread) => ({
        ...thread,
        messages: thread.messages.map((msg: Message) => ({
          ...msg,
          choices: (msg.choices ?? []).map((choice: Choice) => ({
            ...choice,
            ...enhancements[choice.id]
          }))
        }))
      })
    )

    return { success: isSuccess, data: formattedThreads }
  } catch (error) {
    handleApiError(error, 'Failed to fetch chat threads')
    return { success: false, data: [] }
  }
}

export const createNewChatThread = async (
  pageId: string,
  editMode: boolean
) => {
  try {
    const payload = {
      pageId,
      editMode
    }
    const apiClient = await getAxiosApiClientByUser()

    const response = await apiClient.post(
      '/landing-page/threads/create',
      payload
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Failed to create new chat thread')
  }
}